import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Calendar,
  Plus,
  RotateCw,
  Search,
  Edit2,
  Trash2,
  CheckCircle,
  AlertTriangle,
} from "lucide-react";
import {
  getAllAcademicYears,
  getAllAcademicYearsPortal,
  deleteAcademicYear,
  markAsCurrentAcademicYear,
} from "../../api/academicyear/academicYearApi";
import AcademicYearFormModal from "./AcademicYearFormModal";
import {
  Button,
  Input,
  Select,
  Card,
  CardContent,
  Table,
  Badge,
  EmptyState,
  Modal,
} from "../../components/ui";
import { useToast } from "../../context/ToastContext";

const AcademicYearList = ({ isPortal = false }) => {
  const [years, setYears] = useState([]);
  const [loading, setLoading] = useState(true);
  const toast = useToast();

  // Filters
  const [search, setSearch] = useState("");
  const [isActiveFilter, setIsActiveFilter] = useState("");

  // Modal
  const [modalOpen, setModalOpen] = useState(false);
  const [editData, setEditData] = useState(null);

  // Delete confirmation
  const [deleteId, setDeleteId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Set current loading
  const [setCurrentId, setSetCurrentId] = useState(null);

  const fetchYears = useCallback(async () => {
    setLoading(true);
    try {
      const filters = {};
      if (search.trim()) filters.search = search.trim();
      if (isActiveFilter !== "") filters.isActive = isActiveFilter;

      const data = isPortal
        ? await getAllAcademicYearsPortal(filters)
        : await getAllAcademicYears(filters);

      const list = Array.isArray(data)
        ? data
        : data?.data || data?.academicYears || data?.years || [];

      setYears(list);
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err.message ||
        "Failed to load academic years";
      toast.error(message);
      setYears([]);
    } finally {
      setLoading(false);
    }
  }, [search, isActiveFilter, isPortal]);

  useEffect(() => {
    fetchYears();
  }, [fetchYears]);

  const handleCreate = () => {
    setEditData(null);
    setModalOpen(true);
  };

  const handleEdit = (year) => {
    setEditData(year);
    setModalOpen(true);
  };

  const handleDeleteClick = (id) => {
    setDeleteId(id);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteId) return;
    setDeleteLoading(true);
    try {
      await deleteAcademicYear(deleteId);
      toast.success("Academic year deleted successfully");
      setDeleteId(null);
      fetchYears();
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err.message ||
        "Failed to delete academic year";
      toast.error(message);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleSetCurrent = async (id) => {
    setSetCurrentId(id);
    try {
      await markAsCurrentAcademicYear(id);
      toast.success("Academic year marked as current");
      fetchYears();
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err.message ||
        "Failed to set current academic year";
      toast.error(message);
    } finally {
      setSetCurrentId(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    try {
      return new Date(dateStr).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return dateStr;
    }
  };

  const getId = (year) => year._id || year.id;

  const statusOptions = [
    { label: "All Status", value: "" },
    { label: "Active", value: "true" },
    { label: "Inactive / Archived", value: "false" },
  ];

  const columns = useMemo(() => {
    const cols = [
      {
        key: "name",
        header: "Name",
        render: (row) => (
          <div>
            <div className="font-semibold text-neutral-900 dark:text-neutral-100">
              {row.yearName || row.name || "—"}
            </div>
            {(row.description || row.notes) && (
              <div className="mt-0.5 max-w-[240px] truncate text-xs text-neutral-400 dark:text-neutral-500">
                {row.description || row.notes}
              </div>
            )}
          </div>
        ),
      },
      {
        key: "startDate",
        header: "Start Date",
        render: (row) => (
          <span className="whitespace-nowrap text-neutral-600 dark:text-neutral-300">
            {formatDate(row.startDate)}
          </span>
        ),
      },
      {
        key: "endDate",
        header: "End Date",
        render: (row) => (
          <span className="whitespace-nowrap text-neutral-600 dark:text-neutral-300">
            {formatDate(row.endDate)}
          </span>
        ),
      },
      {
        key: "status",
        header: "Status",
        render: (row) => {
          const active =
            row.isActive !== undefined
              ? Boolean(row.isActive)
              : row.status === "Active";
          return (
            <Badge variant={active ? "success" : "default"}>
              {active ? "Active" : "Archived"}
            </Badge>
          );
        },
      },
      {
        key: "current",
        header: "Current",
        render: (row) => {
          const isCurrent =
            row.isCurrent || row.isCurrentYear || row.current;
          return isCurrent ? (
            <Badge variant="primary">Current Year</Badge>
          ) : (
            <span className="text-xs text-neutral-300 dark:text-neutral-600">—</span>
          );
        },
      },
    ];

    if (!isPortal) {
      cols.push({
        key: "actions",
        header: "Actions",
        render: (row) => {
          const id = getId(row);
          const isCurrent =
            row.isCurrent || row.isCurrentYear || row.current;
          return (
            <div className="flex items-center justify-end gap-2">
              {!isCurrent && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleSetCurrent(id)}
                  isLoading={setCurrentId === id}
                  className="text-primary-900 dark:text-primary-300 hover:text-primary-800 dark:hover:text-primary-300"
                >
                  Set Current
                </Button>
              )}
              <Button
                size="sm"
                variant="ghost"
                onClick={() => handleEdit(row)}
                title="Edit"
                aria-label="Edit academic year"
              >
                <Edit2 className="h-4 w-4 text-neutral-500 dark:text-neutral-400 hover:text-primary-900 dark:hover:text-primary-300" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => handleDeleteClick(id)}
                title="Delete"
                aria-label="Delete academic year"
              >
                <Trash2 className="h-4 w-4 text-neutral-500 dark:text-neutral-400 hover:text-danger-600" />
              </Button>
            </div>
          );
        },
      });
    }

    return cols;
  }, [isPortal, setCurrentId]);

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-4 sm:p-6 lg:p-8">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-neutral-900 dark:text-neutral-100 sm:text-3xl">
            Academic Years
          </h1>
          <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
            {isPortal
              ? "View academic years configured for your school"
              : "Manage academic years for your school"}
          </p>
        </div>
        {!isPortal && (
          <Button
            onClick={handleCreate}
            variant="primary"
            leftIcon={<Plus className="h-4 w-4" />}
            className="w-full sm:w-auto"
          >
            Create Academic Year
          </Button>
        )}
      </div>

      {/* Banners */}
      
      

      {/* Filter Card */}
      <Card>
        <CardContent className="p-4 sm:p-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <div className="flex-1">
              <Input
                placeholder="Search by name..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                leftIcon={<Search className="h-4 w-4 text-neutral-400 dark:text-neutral-500" />}
              />
            </div>
            <div className="w-full sm:w-48">
              <Select
                options={statusOptions}
                value={isActiveFilter}
                onChange={(val) => setIsActiveFilter(val)}
              />
            </div>
            <Button
              variant="secondary"
              onClick={fetchYears}
              leftIcon={<RotateCw className="h-4 w-4" />}
              className="w-full sm:w-auto"
            >
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Data Table */}
      <Card>
        <CardContent className="p-0">
          <Table
            columns={columns}
            data={years}
            isLoading={loading}
            emptyState={
              <EmptyState
                icon={Calendar}
                title="No academic years found"
                description={
                  search || isActiveFilter
                    ? "Try adjusting your search or status filters."
                    : isPortal
                    ? "No academic years have been configured yet."
                    : "Create your first academic year to get started."
                }
                action={
                  !isPortal && !search && !isActiveFilter ? (
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={handleCreate}
                      leftIcon={<Plus className="h-4 w-4" />}
                    >
                      Create Academic Year
                    </Button>
                  ) : undefined
                }
              />
            }
          />
        </CardContent>
      </Card>

      {/* Form Modal */}
      <AcademicYearFormModal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditData(null);
        }}
        onSuccess={() => {
          toast.success(
            editData
              ? "Academic year updated successfully"
              : "Academic year created successfully"
          );
          fetchYears();
        }}
        editData={editData}
      />

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={Boolean(deleteId)}
        onClose={() => setDeleteId(null)}
        title="Delete Academic Year?"
        footer={
          <div className="flex w-full items-center justify-end gap-3">
            <Button
              variant="secondary"
              onClick={() => setDeleteId(null)}
              disabled={deleteLoading}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleDeleteConfirm}
              isLoading={deleteLoading}
            >
              Delete
            </Button>
          </div>
        }
      >
        <div className="flex items-start gap-3 py-2">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-danger-50 text-danger-600">
            <AlertTriangle className="h-5 w-5" />
          </div>
          <div>
            <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
              Are you sure you want to delete this academic year?
            </p>
            <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
              This action cannot be undone and may affect linked records.
            </p>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AcademicYearList;